import { useState, useEffect } from "react";
import { Alert, Button, Typography, Spin } from "antd";
import { CheckCircleOutlined, MailOutlined, RetweetOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import supabase from "../../config/supabaseConfig";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";

const { Title, Text, Paragraph } = Typography;

const EmailVerification = () => {
    const [verificationStatus, setVerificationStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
    const [error, setError] = useState('');
    const [email, setEmail] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { width, height } = useWindowSize();

    useEffect(() => {
        const handleEmailVerification = async () => {
            try {
                // Get the access token from the URL
                const accessToken = searchParams.get('access_token') || 
                                  new URLSearchParams(window.location.hash.substring(1)).get('access_token');
                const refreshToken = searchParams.get('refresh_token') || 
                                   new URLSearchParams(window.location.hash.substring(1)).get('refresh_token');

                if (!accessToken || !refreshToken) {
                    throw new Error("Lien de vérification invalide ou expiré");
                }

                // Set the session
                const { data: { session }, error: sessionError } = await supabase.auth.setSession({
                    access_token: accessToken,
                    refresh_token: refreshToken
                });

                if (sessionError) throw sessionError;

                if (session?.user) {
                    setEmail(session.user.email || '');
                    
                    // Create user profile in the database
                    const { error: profileError } = await supabase
                        .from('profiles')
                        .upsert({
                            id: session.user.id,
                            name: session.user.user_metadata.name || session.user.user_metadata.full_name || session.user.email?.split("@")[0] || "User",
                            email: session.user.email,
                            phone: session.user.user_metadata.phone || "",
                            is_verified: true,
                            is_online: true,
                            photo_url: session.user.user_metadata.photo_url || session.user.user_metadata.avatar_url || '',
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString()
                        }, { onConflict: 'id' });

                    if (profileError) throw profileError;

                    setVerificationStatus('success');
                    
                    // Auto redirect after 5 seconds
                    setTimeout(() => {
                        navigate('/auth/login');
                    }, 5000);
                }
            } catch (err) {
                console.error('Verification error:', err);
                setError(err instanceof Error ? err.message : "Une erreur est survenue lors de la vérification");
                setVerificationStatus('error');
            }
        };

        handleEmailVerification();
    }, [searchParams, navigate]);

    const handleResendVerification = async () => {
        try {
            setResendLoading(true);
            setResendSuccess(false);
            
            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: email,
                options: {
                    emailRedirectTo: `${window.location.origin}/auth/verify-email`
                }
            });

            if (error) throw error;

            setResendSuccess(true);
            setError("");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Erreur lors de l'envoi de l'email de vérification");
        } finally {
            setResendLoading(false);
        }
    };

    if (verificationStatus === 'verifying') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-100 p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden"
                >
                    <div className="h-20 bg-gradient-to-r from-blue-600 to-purple-600"></div>
                    <div className="p-8 text-center">
                        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto -mt-16 shadow-lg border-4 border-white">
                            <MailOutlined className="text-blue-600 text-3xl" />
                        </div>
                        
                        <Title level={3} className="mt-4 mb-2">Vérification en cours</Title>
                        <Paragraph className="text-gray-500 mb-6">
                            Nous vérifions votre email. Veuillez patienter un instant...
                        </Paragraph>
                        
                        <div className="flex justify-center">
                            <Spin size="large" />
                        </div>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-100 p-4">
            <AnimatePresence>
                {verificationStatus === 'success' && (
                    <Confetti
                        width={width}
                        height={height}
                        recycle={false}
                        numberOfPieces={500}
                        gravity={0.1}
                    />
                )}
                
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden"
                >
                    <div className={`h-20 ${verificationStatus === 'success' ? 'bg-gradient-to-r from-emerald-500 to-green-500' : 'bg-gradient-to-r from-red-500 to-orange-500'}`}></div>
                    <div className="p-8 text-center">
                        <div className={`w-20 h-20 ${verificationStatus === 'success' ? 'bg-green-100' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto -mt-16 shadow-lg border-4 border-white`}>
                            {verificationStatus === 'success' ? (
                                <CheckCircleOutlined className="text-green-600 text-3xl" />
                            ) : (
                                <MailOutlined className="text-red-600 text-3xl" />
                            )}
                        </div>
                        
                        {verificationStatus === 'success' ? (
                            <>
                                <Title level={3} className="mt-4 mb-2">Email vérifié avec succès!</Title>
                                <Paragraph className="text-gray-500 mb-6">
                                    Votre compte a été activé. Vous pouvez maintenant vous connecter pour accéder à toutes les fonctionnalités.
                                </Paragraph>
                                
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                >
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={() => navigate('/auth/login')}
                                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg h-12 font-medium"
                                    >
                                        Se connecter
                                    </Button>
                                </motion.div>
                                
                                <Text className="block text-gray-400 mt-4">
                                    Redirection automatique dans quelques secondes...
                                </Text>
                            </>
                        ) : (
                            <>
                                <Title level={3} className="mt-4 mb-2">Problème de vérification</Title>
                                <Alert
                                    message="Impossible de vérifier votre email"
                                    description={error || "Le lien de vérification est invalide ou a expiré."}
                                    type="error"
                                    showIcon
                                    className="mb-6 text-left"
                                />
                                
                                {resendSuccess && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        className="mb-6"
                                    >
                                        <Alert
                                            message="Email envoyé avec succès"
                                            description="Veuillez vérifier votre boîte de réception et cliquer sur le lien pour activer votre compte."
                                            type="success"
                                            showIcon
                                            className="text-left"
                                        />
                                    </motion.div>
                                )}
                                
                                {email ? (
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<RetweetOutlined />}
                                        onClick={handleResendVerification}
                                        loading={resendLoading}
                                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg h-12 font-medium"
                                    >
                                        Renvoyer l'email de vérification
                                    </Button>
                                ) : (
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={() => navigate('/auth/register')}
                                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg h-12 font-medium"
                                    >
                                        Retour à l'inscription
                                    </Button>
                                )}
                                
                                <Button
                                    type="link"
                                    onClick={() => navigate('/auth/login')}
                                    className="mt-4 text-gray-500 hover:text-blue-600"
                                >
                                    Retour à la connexion
                                </Button>
                            </>
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default EmailVerification; 